import { queryKeys } from '@doneverse/data/queries'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

/**
 * Re-read `/v1/me` on a timer, for as long as the caller is mounted.
 *
 * The backstop for every onboarding step the founder's tab cannot see close: the
 * webhook-owned ones (1, 3, 8), closed by an integration the founder never sees,
 * and the intake (2), closed by the agent's own call to the API. The realtime
 * `onboarding_step_changed` notification normally moves them within a second.
 * A founder whose socket dropped while they were signing, booking or answering
 * would otherwise sit on a waiting screen forever with the work already done.
 *
 * Invalidation rather than a `refetchInterval` on the profile query itself: the
 * profile is owned by the auth shell and shared by every page, and a waiting
 * surface has no business changing the refetch policy for the whole app.
 *
 * Pass `enabled: false` to hold the timer — a surface that is only sometimes
 * waiting should not poll the rest of the time.
 */
export function useProfilePoll(intervalMs: number, options: { enabled?: boolean } = {}) {
  const enabled = options.enabled ?? true
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled) return

    const timer = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.profile, 'me'] })
    }, intervalMs)
    return () => window.clearInterval(timer)
  }, [enabled, intervalMs, queryClient])
}
